// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Card, CardContent, Typography } from '@mui/material';
import { type AdvisoryProposalType } from '@shared/data-access/interfaces/api/brokerage/client-types';
import { classNames } from '@shared/ui-kit/utils';
import { AppIcon } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import { defineComponent } from '@shared/ui-kit/utils/define-component';

export interface AppropriatenessTestResultProps {
  productFamilies: string[];
  score: number;
  advisoryType?: AdvisoryProposalType;
}

export const AppropriatenessTestResult = defineComponent({
  displayName: 'AppropriatenessTestResult',
  useSetup: ({
    productFamilies,
    score,
    advisoryType,
  }: AppropriatenessTestResultProps) => ({
    productFamilies,
    score,
    advisoryType,
    hasPassed: score >= 49,
  }),
  View: ({ productFamilies, hasPassed, advisoryType }) => {
    return (
      <Card
        variant="outlined"
        className={classNames('!rounded-lg', {
          '!border-2 !border-negative/20': !hasPassed,
        })}
        data-testid="convenience-test-result"
      >
        <CardContent className="!flex !flex-col !gap-4 !p-7">
          <Typography variant="h4" data-testid="convenience-test-title">
            <Translation
              i18nKey="onboarding-process-overview.result-and-signature.convenience-test.title"
              ns="components"
            />
          </Typography>

          {hasPassed ? (
            <>
              <Typography>
                <Translation
                  i18nKey="onboarding-process-overview.result-and-signature.convenience-test.description"
                  ns="components"
                />
              </Typography>

              <ul
                className="flex list-none flex-col gap-2 p-0"
                data-testid="convenience-test-product-families"
              >
                {productFamilies.map(product => (
                  <li
                    key={product}
                    className="grid grid-cols-[1.25rem_1fr] items-start gap-x-3"
                    data-testid={`product-family-${product}`}
                  >
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center pt-0.5">
                      <AppIcon icon="CheckCircle" color="success" size="lg" />
                    </span>
                    <Typography variant="body2" component="span">
                      {product}
                    </Typography>
                  </li>
                ))}
              </ul>

              <Typography className="!text-sm">
                <Translation
                  i18nKey="onboarding-process-overview.result-and-signature.convenience-test.warning"
                  ns="components"
                />
              </Typography>
            </>
          ) : (
            <div className="flex items-start gap-4">
              <div
                className="flex shrink-0 items-center justify-center rounded-lg bg-negative-light p-3"
                data-testid="convenience-test-icon-container"
              >
                <AppIcon
                  icon="Warning"
                  color="danger"
                  size={24}
                  data-testid="convenience-test-icon"
                />
              </div>

              <div
                className="flex flex-1 flex-col items-start gap-2"
                data-testid="convenience-test-rejection-info"
              >
                <Typography
                  variant="body1"
                  className="!text-danger"
                  data-testid="convenience-test-rejection-message"
                >
                  <Translation
                    i18nKey={`onboarding-process-overview.result-and-signature.convenience-test.rejection-message${
                      advisoryType === 'discretionary' ? '-discretionary' : ''
                    }`}
                    ns="components"
                  />
                </Typography>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  },
});
